"use client";
import isEmpty from "lodash/isEmpty";
import { useMemo, useState, useEffect } from "react";
import snakeCase from "lodash/snakeCase";

import {
  DropdownItem,
  DropdownToggle,
  ResponsiveDrawerDropdown,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";
import { CheckboxField } from "@stadium/ui/src/components/input/bs-input";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { useFlags, withSyncFeatureFlag } from "@stadium/services/launch-darkly";

import { getConversionRates, getCurrencies } from "@stadium/redux-utils/selectors/configSelector";

import { convertPrice, formatPrice } from "@stadium/helpers/currency";
import { useCheckoutContext } from "@stadium/checkout/src/contexts/checkout";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { DEFAULT_STC_CURRENCY } from "@stadium/constants/currency";
import { USE_WALLET_OPTIONS } from "@stadium/checkout/src/constants/wallet";
import { RECIPIENT_ORDER_TYPE } from "@stadium/checkout/src/constants/order";
import { PAYMENT_METHOD_TARGET } from "@stadium/checkout/src/constants/analytics";
import { workspaceWalletFlag } from "@stadium/services/launch-darkly/constants";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";

import style from "./main.module.scss";

const {
  useWalletMoney: useWalletObj,
  useGlobalPoint: useGlobalPointsObj,
  useStoreSpecificPoint: useStoreSpecificPointsObj,
} = USE_WALLET_OPTIONS;

const defaultArr = [];
const emptyObj = {};

const Wallet = ({
  defaultValues = emptyObj,
  orderTotal,
  orderTotalForInsufficientFundsCheck,
  billingCurrencyCode,
  isExistingOrder = false,
  teamWallets = defaultArr,
  showStadiumPoints = true,
  showWalletGroup = true,
  isSingleSourcePayment = false,
  storeDetails = emptyObj,
  disableCollapse = false,
  isTeamWalletsLoading = false,
  eventLocationAnalytics = emptyObj,
}) => {
  const { [workspaceWalletFlag]: workspaceWalletFlagEnabled } = useFlags();
  const { defaultUseStoreSpecificPoints, isWalletAccordionOpen = true } = defaultValues;

  const {
    useStadiumPoints,
    setUseStadiumPoints,
    useStoreSpecificPoints,
    setUseStoreSpecificPoints,
    stadiumPointsBalance,
    walletCurrency,
    userWalletBalance,
    useWallet,
    setUseWallet,
    selectedTeamWallet,
    setSelectedTeamWallet,
    isPaymentPending,
    formattedStadiumPointsBalance,
    storeSpecificPointsBalance,
    isSingleSourcePaymentMethodSelected,
    setIsTeamWalletSelectedByDefault,
    orderData,
    isPersonalWorkspace,
  } = useCheckoutContext();

  const withAnalytics = useWithAnalytics();

  const { teamId: defaultTeamWalletId } = orderData;
  const isOpenOrder = !!orderData?.allowAnonymousInvite && orderData?.type === RECIPIENT_ORDER_TYPE;
  const { storeCurrencyName, storeCurrencyCode, storeCurrencyPrefix, storeCurrencySuffix } =
    storeDetails;

  const isExistingOrderWithSingleSourcePaymentMethod =
    (isSingleSourcePaymentMethodSelected || !!defaultTeamWalletId) && isExistingOrder;

  const conversionRates = useConfig(getConversionRates);
  const currencies = useConfig(getCurrencies);

  const [isWalletOpen, setIsWalletOpen] = useState(isWalletAccordionOpen);
  const [selectedWallet, setSelectedWallet] = useState(emptyObj);

  const toggleStadiumFundsAccordion = () => setIsWalletOpen((prev) => !prev);

  const amountForInsufficientCheck = orderTotalForInsufficientFundsCheck ?? orderTotal;

  const checkForInsufficientFunds = (balance, currencyCode) => {
    const orderTotalInWalletCurrency = convertPrice({
      from: billingCurrencyCode,
      to: currencyCode,
      value: amountForInsufficientCheck,
      conversionRates,
    });
    return orderTotalInWalletCurrency > balance;
  };

  const walletOptions = useMemo(() => {
    if (!walletCurrency?.id || isTeamWalletsLoading) return [];
    const wallets = [...teamWallets];
    if (userWalletBalance)
      wallets.push({
        id: useWalletObj.key,
        name: useWalletObj.label,
        currencyCode: walletCurrency.code,
        myWalletBalance: userWalletBalance,
      });

    return wallets.map((wallet) => {
      const { id, currencyCode, myWalletBalance } = wallet;
      const isTeamWallet = id !== useWalletObj.key,
        insufficentFunds = checkForInsufficientFunds(myWalletBalance, currencyCode),
        isDisabled =
          ((isTeamWallet || isSingleSourcePayment) && (insufficentFunds || isExistingOrder)) ||
          isExistingOrderWithSingleSourcePaymentMethod ||
          (isTeamWallet && isOpenOrder),
        isExistingOrderDefaultSelectedTeamWallet = isExistingOrder && +defaultTeamWalletId === +id;

      return {
        ...wallet,
        isDisabled: isExistingOrderDefaultSelectedTeamWallet ? false : isDisabled,
      };
    });
  }, [
    orderTotal,
    orderTotalForInsufficientFundsCheck,
    teamWallets,
    userWalletBalance,
    walletCurrency,
    storeSpecificPointsBalance,
    isSingleSourcePayment,
    isExistingOrder,
    isExistingOrderWithSingleSourcePaymentMethod,
    defaultUseStoreSpecificPoints,
    isTeamWalletsLoading,
  ]);

  const currencyMapping = useMemo(() => {
    const mapping = {};
    currencies.forEach((currency) => {
      mapping[currency.code] = currency;
    });
    return mapping;
  }, [currencies]);

  const checkForInsufficientFundsInWallets = () =>
    walletOptions.every(({ isDisabled }) => isDisabled);

  useEffect(() => {
    setIsTeamWalletSelectedByDefault(!!defaultTeamWalletId);
  }, [defaultTeamWalletId]);

  useEffect(() => {
    if (selectedTeamWallet?.id === selectedWallet.id && !isEmpty(walletOptions)) {
      const isSelectedTeamWalletDisabled = walletOptions.find(
        ({ id }) => id === selectedTeamWallet?.id,
      )?.isDisabled;
      if (isSelectedTeamWalletDisabled) {
        setInitialWalletStates();
        handleWalletChange(false);
      }
    }
  }, [selectedTeamWallet, selectedWallet, walletOptions]);

  const setInitialWalletStates = () => {
    const defaultTeamWallet = walletOptions.find(
      ({ id }) => id.toString() === defaultTeamWalletId?.toString(),
    );

    const isDefaultTeamWalletDisabled = defaultTeamWallet?.isDisabled ?? true;

    if (defaultTeamWalletId) {
      setSelectedTeamWallet(isDefaultTeamWalletDisabled ? null : defaultTeamWallet);
    }

    const wallet =
      walletOptions.findLast(({ id, isDisabled }) => {
        if (isDisabled) return false;
        if (defaultTeamWalletId) return id.toString() === defaultTeamWalletId.toString();
        return true;
      }) || walletOptions.slice(-1)?.[0];

    setSelectedWallet(wallet || emptyObj);
  };

  useEffect(() => {
    if (isEmpty(walletOptions) || (!isSingleSourcePayment && !isEmpty(selectedWallet))) return;

    setInitialWalletStates();
  }, [walletOptions]);

  const storeSpecificPointsProps = {
    showCheckbox: +storeSpecificPointsBalance,
    pointsObj: { ...useStoreSpecificPointsObj, label: `Shop ${storeCurrencyName}` },
    checked: useStoreSpecificPoints,
    onChangeCheckbox: setUseStoreSpecificPoints,
    pointsBalance: formatPrice(storeSpecificPointsBalance, {
      prefix: storeCurrencyPrefix,
      suffix: storeCurrencySuffix,
      currencyCode: storeCurrencyCode,
    }),
    disabled:
      isPaymentPending ||
      isOpenOrder ||
      (isSingleSourcePayment
        ? checkForInsufficientFunds(stadiumPointsBalance, DEFAULT_STC_CURRENCY.code)
        : isExistingOrderWithSingleSourcePaymentMethod),
  };

  const stadiumPointsCheckboxProps = {
    showCheckbox: stadiumPointsBalance && showStadiumPoints,
    pointsObj: useGlobalPointsObj,
    checked: useStadiumPoints,
    onChangeCheckbox: setUseStadiumPoints,
    pointsBalance: formattedStadiumPointsBalance,
    disabled:
      isPaymentPending ||
      (isSingleSourcePayment
        ? checkForInsufficientFunds(stadiumPointsBalance, DEFAULT_STC_CURRENCY.code)
        : isExistingOrderWithSingleSourcePaymentMethod),
  };

  const walletCheckBoxProps = {
    showCheckbox: userWalletBalance,
    pointsObj: useWalletObj,
    checked: useWallet,
    onChangeCheckbox: setUseWallet,
    pointsBalance: formatPrice(userWalletBalance || 0, {
      prefix: currencyMapping[walletCurrency?.code]?.symbol || walletCurrency?.code,
    }),
    disabled:
      isPaymentPending ||
      (isSingleSourcePayment
        ? checkForInsufficientFunds(userWalletBalance, walletCurrency.code)
        : isExistingOrderWithSingleSourcePaymentMethod || !userWalletBalance),
  };

  const renderPointsCheckbox = ({
    showCheckbox = true,
    pointsObj,
    checked,
    onChangeCheckbox = () => emptyObj,
    pointsBalance,
    disabled,
  }) => {
    if (!showCheckbox) return null;

    const checkBoxIconClass = checked ? style["iconCheckboxFill"] : style["iconCheckboxDefault"];
    const radioIconClass = checked ? style.radioChecked : style.styleRadio;
    const iconClass = isSingleSourcePayment ? radioIconClass : checkBoxIconClass;

    return (
      <div className={`${style.customCheckbox} ${disabled ? style.disabled : ""}`}>
        <CheckboxField
          id={pointsObj.key}
          checked={checked}
          onChange={() => {
            const isSelected = !checked || isSingleSourcePayment;
            onChangeCheckbox(isSelected);
            if (eventLocationAnalytics?.track) {
              withAnalytics(undefined, {
                ...eventLocationAnalytics,
                target: eventLocationAnalytics?.target ?? PAYMENT_METHOD_TARGET,
                action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_CHANGED,
                eventProperties: {
                  method: snakeCase(pointsObj.label || ""),
                  is_selected: isSelected,
                  ...eventLocationAnalytics?.eventProperties,
                },
              })();
            }
          }}
          customClass="mr-2"
          disabled={disabled}
        />
        <label htmlFor={pointsObj.key} tabIndex={0}>
          <aside className={`${style.iconCheckbox} ${iconClass}`} />
          <div>
            <div className={style.headingInfo}>{pointsObj.label}</div>
            <span className={style.extraInfo}>Available Balance: {pointsBalance}</span>
          </div>
        </label>
      </div>
    );
  };

  const handleWalletChange = (checked, wallet = selectedWallet) => {
    let method = "";
    if (wallet.id === useWalletObj.key) {
      setUseWallet(checked);
      method = "my_wallet";
    } else {
      setSelectedTeamWallet(checked ? wallet : null);
      method = "team_wallet";
    }
    if (eventLocationAnalytics?.track) {
      withAnalytics(undefined, {
        ...eventLocationAnalytics,
        target: eventLocationAnalytics?.target ?? PAYMENT_METHOD_TARGET,
        action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_CHANGED,
        eventProperties: {
          method,
          is_selected: checked,
          ...eventLocationAnalytics?.eventProperties,
        },
      })();
    }
  };

  const renderWalletLabel = ({ id, name, currencyCode, myWalletBalance }) => {
    const prefix = currencyMapping[currencyCode]?.symbol || currencyCode,
      balance = formatPrice(myWalletBalance || 0, { prefix }),
      insufficentFunds = checkForInsufficientFunds(myWalletBalance, currencyCode),
      isInSufficientBalanceTeamWallet = id !== useWalletObj.key && insufficentFunds;

    return (
      <div className={isInSufficientBalanceTeamWallet ? style.insufficientFundBlock : ""}>
        <div className={style.headingInfo}>{name}</div>
        <span className={style.extraInfo}>
          <span className={isInSufficientBalanceTeamWallet ? style.insufficientFund : ""}>
            {isInSufficientBalanceTeamWallet ? "Insufficient" : "Available"} Balance:
          </span>{" "}
          {balance}
        </span>
      </div>
    );
  };

  const handleWalletDropdownSelect = (wallet) => {
    handleWalletChange(true, wallet);
    setSelectedWallet(wallet);
  };

  const renderWalletDropdownItem = (wallet) => {
    const { id, isDisabled } = wallet;

    return (
      <DropdownItem
        key={id}
        eventKey={id}
        onSelect={() => handleWalletDropdownSelect(wallet)}
        disabled={isDisabled}
        className={style.dropdownItem}
      >
        {renderWalletLabel(wallet)}
      </DropdownItem>
    );
  };

  const renderWalletGroup = () => {
    if (isEmpty(walletOptions)) return null;

    const isWalletGroupDisabled = isSingleSourcePayment
      ? checkForInsufficientFundsInWallets()
      : isPaymentPending || isExistingOrder;
    const isChecked = useWallet || selectedTeamWallet?.id === selectedWallet.id;
    const selectedWalletCurrentDisabled =
      walletOptions.find(({ id }) => id?.toString?.() === selectedWallet?.id?.toString?.())
        ?.isDisabled ?? selectedWallet?.isDisabled;
    const checkBoxIconClass = isChecked ? style["iconCheckboxFill"] : style["iconCheckboxDefault"];
    const radioIconClass = isChecked ? style.radioChecked : style.styleRadio;
    const iconClass = isSingleSourcePayment ? radioIconClass : checkBoxIconClass;
    return (
      <div
        className={`${style.customCheckbox} ${style.walletDropdownWrapper} ${
          isWalletGroupDisabled ? style.disabled : ""
        }`}
      >
        <label
          htmlFor="wallet"
          tabIndex={0}
          className={selectedWalletCurrentDisabled ? style.labelDisabled : ""}
        >
          <CheckboxField
            id="wallet"
            checked={isChecked}
            onChange={() => handleWalletChange(!isChecked || isSingleSourcePayment)}
            customClass="mr-2"
            disabled={isWalletGroupDisabled || selectedWalletCurrentDisabled}
          />
          <aside className={`${style.iconCheckbox} ${iconClass}`} />
        </label>
        <ResponsiveDrawerDropdown
          className={style.walletDropdown}
          menuClassName={style.walletDropDownMenu}
          heading={<DropdownToggle>Choose your wallet</DropdownToggle>}
          label={
            <DropdownToggle
              className={`${style.dropdownHeader} ${isChecked ? style.withChecked : ""}`}
            >
              {renderWalletLabel(selectedWallet)}
            </DropdownToggle>
          }
        >
          {walletOptions.map(renderWalletDropdownItem)}
          {(!workspaceWalletFlagEnabled || !isPersonalWorkspace) && !isEmpty(teamWallets) && (
            <DropdownItem key="teamWalletInfo" disabled className={style.dropdownItemSpan}>
              <div>
                {workspaceWalletFlagEnabled ? "" : "Shared"} Wallets cannot be combined with other
                payment methods.
              </div>
            </DropdownItem>
          )}
        </ResponsiveDrawerDropdown>
      </div>
    );
  };

  const renderWalletOptions = () => {
    if (!isWalletOpen) return null;

    return (
      <>
        {renderPointsCheckbox(storeSpecificPointsProps)}
        {renderPointsCheckbox(stadiumPointsCheckboxProps)}
        {showWalletGroup && (
          <div className={style.walletWrapper}>
            {isEmpty(teamWallets) ? renderPointsCheckbox(walletCheckBoxProps) : renderWalletGroup()}
          </div>
        )}
      </>
    );
  };

  if (isTeamWalletsLoading) return <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-363" />;

  if (
    isEmpty(walletOptions) &&
    (!stadiumPointsBalance || !showStadiumPoints) &&
    !storeSpecificPointsBalance
  )
    return null;

  if (disableCollapse) {
    return (
      <div className={style.toggleDisable}>
        <div className={style.accordionHeader}>
          My Stadium Funds
          <div className={style.collapseIconBlock}>
            <span
              className={` ${style.arrowIcon} ${isWalletOpen ? style.iconUp : style.iconDown}`}
            />
          </div>
        </div>
        <div
          className={`${style.collapseBlock} ${
            storeSpecificPointsBalance ? style.collapseFlexRow : ""
          }`}
        >
          {renderWalletOptions()}
        </div>
      </div>
    );
  }

  return (
    <AccordionAllCollapsable
      id=""
      isOpen={isWalletOpen}
      handleAccordianToggle={toggleStadiumFundsAccordion}
      accordionCustomClass={style.accordion}
      titleContent={
        <div className={style.accordionHeader}>
          My Stadium Funds
          <div className={style.collapseIconBlock}>
            <span
              className={` ${style.arrowIcon} ${isWalletOpen ? style.iconUp : style.iconDown}`}
            />
          </div>
        </div>
      }
      bodyContent={
        <div
          className={`${style.collapseBlock} ${
            storeSpecificPointsBalance ? style.collapseFlexRow : ""
          }`}
        >
          {renderWalletOptions()}
        </div>
      }
    />
  );
};

export default withSyncFeatureFlag(Wallet);
